import React, { useEffect, useMemo, useState } from 'react';
import { Link, Navigate, useParams, useSearchParams } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import SEOHead from '../components/SEOHead';
import NewsFilterTabs from '../components/NewsFilterTabs';
import NewsPublicationCard from '../components/NewsPublicationCard';
import NewsModal from '../components/NewsModal';
import {
  NEWS_HUB_PATH,
  buildNewsHubPath,
  buildNewsHubStructuredData,
  getCategoryMeta,
  getNewsHubSeo,
  parseCategorySlug,
  parseNewsFilter,
} from '../lib/newsPublications';
import { fetchPublications } from '../lib/newsPublicationsApi';

const NovostiAntologiaPage = () => {
  const { categorySlug } = useParams();
  const [searchParams] = useSearchParams();
  const [selectedItem, setSelectedItem] = useState(null);
  const [visibleItems, setVisibleItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const legacyFilter = searchParams.get('filter');
  const activeCategoryId = categorySlug ? parseCategorySlug(categorySlug) : 'all';

  useEffect(() => {
    let active = true;
    setLoading(true);
    fetchPublications(activeCategoryId || 'all')
      .then((items) => {
        if (active) setVisibleItems(items);
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => { active = false; };
  }, [activeCategoryId]);

  const structuredData = useMemo(
    () => buildNewsHubStructuredData(activeCategoryId || 'all', visibleItems),
    [activeCategoryId, visibleItems],
  );

  if (legacyFilter && !categorySlug) {
    return <Navigate to={buildNewsHubPath(parseNewsFilter(legacyFilter))} replace />;
  }

  if (categorySlug && !activeCategoryId) {
    return <Navigate to={NEWS_HUB_PATH} replace />;
  }

  const categoryMeta = getCategoryMeta(activeCategoryId);
  const seo = getNewsHubSeo(activeCategoryId);

  return (
    <div className="min-h-screen flex flex-col bg-background">
      <SEOHead
        title={seo.title}
        description={seo.description}
        keywords={seo.keywords}
        canonical={seo.canonical}
        structuredData={structuredData}
      />
      <Header />

      <main className="flex-1 pt-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 sm:py-14">
          <nav className="text-sm text-muted-foreground mb-6" aria-label="Хлебные крошки">
            <ol className="flex flex-wrap items-center gap-2">
              <li>
                <Link to="/" className="hover:text-accent transition-colors">
                  Главная
                </Link>
              </li>
              <li aria-hidden>/</li>
              <li>
                {activeCategoryId === 'all' ? (
                  <span className="text-foreground">Новости</span>
                ) : (
                  <Link to={NEWS_HUB_PATH} className="hover:text-accent transition-colors">
                    Новости
                  </Link>
                )}
              </li>
              {activeCategoryId !== 'all' && (
                <>
                  <li aria-hidden>/</li>
                  <li>
                    <span className="text-foreground">{categoryMeta.label}</span>
                  </li>
                </>
              )}
            </ol>
          </nav>

          <div className="mb-8 sm:mb-10">
            <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold mb-4">{seo.h1}</h1>
            <p className="text-lg text-muted-foreground max-w-3xl">{seo.intro}</p>
          </div>

          <div className="mb-10">
            <NewsFilterTabs activeFilter={activeCategoryId} />
          </div>

          {loading ? (
            <p className="text-muted-foreground">Загрузка публикаций...</p>
          ) : visibleItems.length > 0 ? (
            <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
              {visibleItems.map((item) => (
                <NewsPublicationCard
                  key={item.id}
                  item={item}
                  onOpen={setSelectedItem}
                />
              ))}
            </div>
          ) : (
            <div className="rounded-xl border border-dashed border-border/70 bg-muted/20 px-6 py-16 text-center">
              <p className="text-lg font-medium mb-2">Пока нет материалов в этой рубрике</p>
              <p className="text-muted-foreground mb-6">
                Мы готовим публикации — загляните позже или выберите другой раздел.
              </p>
              <Link
                to={NEWS_HUB_PATH}
                className="text-accent hover:underline font-medium"
              >
                Смотреть все материалы
              </Link>
            </div>
          )}
        </div>
      </main>

      <Footer />

      <NewsModal
        news={selectedItem}
        isOpen={Boolean(selectedItem)}
        onClose={() => setSelectedItem(null)}
      />
    </div>
  );
};

export default NovostiAntologiaPage;
